import { DeviceType } from '@services/constants'
import {
  ChargingPointDetailStatusColor,
  EStatusPill,
  IStatisticalByDeviceType,
} from '@services/device'
import { ISession } from '@services/session'
import useVisible from '@shared/innmaxLib/hooks/useVisible'
import { toThousandSeparator } from '@shared/innmaxLib/utils/webHelper'
import { ISessionMarkerProps } from '@shared/InnmaxMap2/map.types'
import { Col, Row } from 'antd'
import { pluck, sum } from 'ramda'
import React from 'react'
import styled from 'styled-components'
import Popup from '../Popup'
import Marker, { IEnhanceMarkerRef } from './Marker'

// interface ISessionMarkerProps
//   extends Omit<IMapLibreMarkerProps, 'onDelete' | 'onDragEnd' | 'onLeftClick'> {
//   session: ISession
//   deviceCountInfo?: {
//     deviceCount: number
//     deviceAlarmCount: number
//     deviceRepairCount: number
//     deviceNormalCount: number
//   }
//   onDelete?: (d: ISession) => void
//   onDragEnd?: (d: ISession) => void
//   onLeftClick: (d: ISession) => void
//   size?: 'small' | 'large'
// }

const SessionMarker = React.memo((props: ISessionMarkerProps) => {
  const {
    session,
    deviceCountInfo = {
      deviceCount: 0,
      deviceUnavailableCount: 0,
      deviceAvailableCount: 0,
      deviceChargingCount: 0,
    },
    statistical,
    onDelete,
    onDragEnd,
    onLeftClick,
    size,
    ...others
  } = props

  const markerRef = React.useRef<IEnhanceMarkerRef>(null)

  const tooltipWindow = useVisible()

  React.useEffect(() => {
    markerRef.current?.setPosition({
      lat: session?.lat,
      lon: session?.lon,
      x: session?.x,
      y: session?.y,
    })
  }, [session])

  const sumConnectorDetail = (key: keyof IStatisticalByDeviceType) =>
    toThousandSeparator(
      sum(
        pluck(
          key as any,
          statistical?.filter(
            item => item?.deviceType === DeviceType.CONNECTOR
          ) || []
        )
      )
    )

  const handleMouseOver = React.useCallback(() => {
    tooltipWindow.open()
  }, [])

  const handleMouseOut = React.useCallback(() => {
    tooltipWindow.close()
  }, [])
  console.log('tooltipWindow', tooltipWindow.visible)
  return Marker.Utils.isValidMarkerPosition(session) ? (
    <Marker
      {...others}
      ref={markerRef}
      tooltipContent={<span>{session?.name}</span>}
      onDelete={() => onDelete && onDelete(session)}
      onLeftClick={() => {
        onLeftClick && onLeftClick(session)
      }}
      defaultPosition={{
        lat: session?.lat,
        lon: session?.lon,
        x: session?.x,
        y: session?.y,
      }}
      onDragEnd={position => {
        onDragEnd &&
          onDragEnd({
            ...session,
            ...position,
          } as ISession)
      }}>
      <Marker.MarkerIconDiv
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}>
        <StyledMarkerWrapper>
          <div className="text-xl font-semibold truncate w-120 text-center">
            {session.name}
          </div>
          <div className="text-lg font-semibold text-white">
            {toThousandSeparator(deviceCountInfo.deviceCount)}
          </div>
          <div className="font-medium text-lg">
            <span className="text-white">
              {toThousandSeparator(
                deviceCountInfo.deviceAvailableCount +
                  deviceCountInfo.deviceChargingCount
              )}{' '}
              |{' '}
            </span>
            <span>
              {toThousandSeparator(deviceCountInfo.deviceUnavailableCount)}
            </span>
          </div>
        </StyledMarkerWrapper>
      </Marker.MarkerIconDiv>
      {tooltipWindow.visible && (
        <Popup visible>
          <DetailPanel>
            <Row className="content">
              <Col span={12} className="text-avaliable">
                可使用
              </Col>
              <Col span={12} className="text-white">
                {sumConnectorDetail('availableCount')}
              </Col>
              <Col span={12} className="text-reserved">
                預定中
              </Col>
              <Col span={12} className="text-white">
                {sumConnectorDetail('reservedCount')}
              </Col>
              <Col span={12} className="text-charging">
                準備充電
              </Col>
              <Col span={12} className="text-white">
                {sumConnectorDetail('preparingCount')}
              </Col>
              <Col span={12} className="text-charging">
                充電中
              </Col>
              <Col span={12} className="text-white">
                {sumConnectorDetail('chargingCount')}
              </Col>
              <Col
                span={12}
                style={{
                  color: ChargingPointDetailStatusColor[EStatusPill.Occupy],
                }}>
                占用中
              </Col>
              <Col span={12} className="text-white">
                {sumConnectorDetail('finishingCount')}
              </Col>
              <Col span={12} className="text-unavaliable">
                充電樁停止
              </Col>
              <Col span={12} className="text-white">
                {sumConnectorDetail('suspendedEVSECount')}
              </Col>
              <Col span={12} className="text-unavaliable">
                車輛停止
              </Col>
              <Col span={12} className="text-white">
                {sumConnectorDetail('suspendedEVCount')}
              </Col>
              <Col span={12} className="text-[#859FAA]">
                無服務
              </Col>
              <Col span={12} className="text-white">
                {sumConnectorDetail('unavailableCount')}
              </Col>
              <Col span={12} className="text-unavaliable">
                故障
              </Col>
              <Col span={12} className="text-white">
                {sumConnectorDetail('faultedCount')}
              </Col>
            </Row>
          </DetailPanel>
        </Popup>
      )}
    </Marker>
  ) : (
    <div></div>
  )
})

export default SessionMarker

const StyledMarkerWrapper = styled(({ ...props }) => <div {...props} />)`
  width: 130px;
  height: 130px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgb(255, 204, 43, 0.8);
  margin-left: -65px;
  margin-top: -65px;

  position: relative;

  &::before {
    content: '';
    position: absolute;
    width: 180px;
    height: 180px;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      rgba(255, 204, 43, 0.1) 50%,
      rgba(255, 204, 43, 0.3) 60%,
      transparent 100%
    );
    filter: blur(5px);
    z-index: -2;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &::after {
    content: '';
    position: absolute;
    width: 160px;
    height: 160px;
    border-radius: 50%;
    border: 3px solid rgba(255, 204, 43, 0.3);
    box-shadow: 0 0 3px 3px rgba(255, 204, 43, 0.3);
    z-index: -1;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  &:hover {
    z-index: 1000;
    &::after {
      border-color: rgba(255, 204, 43, 0.8);
    }
  }
`

const DetailPanel = styled.div`
  position: relative;
  z-index: 99999;
  width: 180px;
  background: rgba(92, 99, 101, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  display: block;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);

  p {
    margin: 0;
  }
  > .summary {
    background-color: rgba(255, 255, 255, 0.1);
  }
  .desc {
    color: rgba(255, 255, 255, 0.5);
  }
  .content,
  .summary {
    padding: 10px 15px;
    .ant-col:nth-child(2n) {
      text-align: right;
    }
  }
`
